import React, { useRef, useState } from "react";
import { Camera, X, Lock } from "lucide-react";
import { compressAvatarFile } from "@/lib/avatarImage";
import { toast } from "sonner";

// Avatar image picker / preview. Props:
//   value          current data URL (or falsy)
//   onChange(v)    called with new data URL or "" to clear
//   tier           "free" | "plus" | "grand" — controls gating
//   size           px of the preview circle (default 44)
//   onUpgradeClick optional handler shown when the user taps an upgrade chip
//
// For Free plans: shows a dimmed "Plus only" chip. Tapping it calls
// onUpgradeClick so the host can open billing.
export default function AvatarImagePicker({
  value,
  onChange,
  tier = "free",
  size = 44,
  onUpgradeClick,
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const paid = tier === "plus" || tier === "grand";

  const openPicker = () => {
    if (!paid) {
      if (onUpgradeClick) onUpgradeClick();
      else toast.info("Photo avatars need the Plus or Grand plan.");
      return;
    }
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!f) return;
    setBusy(true);
    try {
      const dataUrl = await compressAvatarFile(f);
      onChange(dataUrl);
    } catch (err) {
      toast.error(err?.message || "Couldn't read that image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {value ? (
        <div
          className="relative flex-shrink-0"
          style={{ width: size, height: size }}
        >
          <img
            src={value}
            alt="avatar preview"
            className="w-full h-full rounded-full object-cover border border-[color:var(--line)]"
            data-testid="avatar-image-preview"
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[color:var(--ink)] text-[color:var(--bg)] flex items-center justify-center"
            data-testid="avatar-image-clear"
            aria-label="Remove photo"
          >
            <X className="w-3 h-3" strokeWidth={2.2} />
          </button>
        </div>
      ) : null}

      <button
        type="button"
        onClick={openPicker}
        disabled={busy}
        className={`ff-chip inline-flex items-center gap-1.5 ${
          !paid ? "opacity-70" : ""
        }`}
        data-testid="avatar-image-pick"
      >
        {!paid ? <Lock className="w-3.5 h-3.5" strokeWidth={1.75} /> : <Camera className="w-3.5 h-3.5" strokeWidth={1.75} />}
        {busy ? "Compressing…" : value ? "Change photo" : "Photo"}
        {!paid && (
          <span className="ml-1 text-[10px] tracking-wider uppercase text-[color:var(--ink-3)]">
            Plus
          </span>
        )}
      </button>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid="avatar-image-input"
      />
    </div>
  );
}
